'use client'

import { Heading, cn } from '@desoutter/uikit'
import { VideoBlockFragment } from './VideoBlock.generated'
import { useMemo, useState } from 'react'
import Image from 'next/image'

type VideoBlockProps = {
	fragment: VideoBlockFragment
	className?: string
}

type VideoCard = Pick<
	VideoBlockFragment['video'],
	'url' | 'title' | 'thumbnailUrl'
> & {
	id: number
}

export default function VideoBlock({ fragment, className }: VideoBlockProps) {
	const videos: VideoCard[] = [fragment.video, ...fragment.otherVideos].map(
		(video, index) => {
			if (!video) return
			if (video.__typename === 'ExternalVideoRecord') {
				return {
					...video.video,
					id: index + 1,
				}
			}
			return {
				...video,
				id: index + 1,
			}
		},
	)
	const [selectedVideo, setSelectedVideos] = useState<VideoCard>(videos[0])

	const onClick = (video: VideoCard) => {
		setSelectedVideos(video)
	}

	return (
		<div
			id={fragment.anchor ? 'spy_' + fragment.anchor.replaceAll(' ', '_') : ''}
			className={fragment.isDark ? 'dark' : ''}
		>
			<div className="bg-background text-foreground">
				<div className="max-w-[800px] mx-5 sm:mx-auto py-16">
					{fragment.title && (
						<Heading as="h2" className="h3 w-full text-center">
							{fragment.title}
						</Heading>
					)}
					{fragment.subtitle && (
						<p className="h5 w-full text-center">{fragment.subtitle}</p>
					)}
					{selectedVideo ? (
						<iframe
							className={cn(
								'w-full rounded-2xl',
								{ 'mt-8': !!fragment.title || !!fragment.subtitle },
								className,
							)}
							height="450"
							src={selectedVideo.url.replace('/watch?v=', '/embed/')}
							title={fragment.video.title}
							allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
							allowFullScreen
						/>
					) : null}
					{videos.length > 1 ? (
						<div className="flex justify-center mt-6 gap-4">
							{videos.map((video) => {
								return (
									<div
										role="button"
										key={video.id}
										onClick={() => onClick(video)}
										aria-current={selectedVideo.id === video.id}
										className="apsect-ratio aspect-video w-full md:max-w-[200px] overflow-hidden rounded-xl cursor-pointer aria-[current=true]:outline-2 aria-[current=true]:outline-foreground aria-[current=true]:outline brightness-75"
									>
										<Image
											src={video.thumbnailUrl}
											alt={video.title}
											className="object-cover size-full"
											width={200}
											height={150}
										/>
									</div>
								)
							})}
						</div>
					) : null}
				</div>
			</div>
		</div>
	)
}
